import {
  LendingPaymentOptionDetails,
  LendingPaymentSource,
  LendingPaymentSourceDisplayGroup,
  LendingPaymentTypeInput,
  PlaidEntryPointInput,
  PlaidProduct,
} from '@varo/graphql-schema-types';
import { useState } from 'react';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { PlaidIntent } from 'shared/hooks/usePlaidV2/types';
import usePlaidV2 from 'shared/hooks/usePlaidV2/usePlaidV2';

import useAdvancePaymentOptions from './useAdvancePaymentOptions';
import usePaymentOptionsPolling from './usePaymentOptionsPolling';

interface UseAdvancePaymentAccountControllerProps {
  paymentType: LendingPaymentTypeInput;
  plaidEntryPoint: PlaidEntryPointInput;
}

const getDefaultSelectedAccount = (
  checkingAccount: LendingPaymentOptionDetails | undefined,
  savingsAccount: LendingPaymentOptionDetails | undefined,
) => {
  const checkingAccountBalance = Number(checkingAccount?.balance?.amount) || 0;
  const savingsAccountBalance = Number(savingsAccount?.balance?.amount) || 0;

  if (checkingAccountBalance > 0) {
    return checkingAccount;
  }

  if (savingsAccountBalance > 0) {
    return savingsAccount;
  }

  return checkingAccount ?? savingsAccount;
};

const useAdvancePaymentAccountController = ({
  paymentType,
  plaidEntryPoint,
}: UseAdvancePaymentAccountControllerProps) => {
  const [selectedAccountId, setSelectedAccountId] = useState<string>();
  const {
    options,
    loading,
    error: paymentOptionsError,
    refetch,
  } = useAdvancePaymentOptions(paymentType);

  const {
    openPlaid,
    ready,
    fetching,
    error: plaidError,
  } = usePlaidV2({
    intent: PlaidIntent.Link,
    product: PlaidProduct.Auth,
    entryPoint: plaidEntryPoint,
  });

  const {
    refreshTiles,
    fetching: fetchingHomeTiles,
    error: homeTilesError,
  } = useRefreshHomeTiles([
    HomeTileTypeIds.LINKED_ACCOUNTS_TILE,
    HomeTileTypeIds.EXTERNAL_ACCOUNTS_TILE,
    HomeTileTypeIds.LIST_ITEM_MANAGE_EXTERNAL_ACCOUNTS,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
  ]);

  const {
    startPolling,
    isPolling,
    error: pollingError,
  } = usePaymentOptionsPolling(refetch);

  const startLinkAccount = async () => {
    const result = await openPlaid();
    if (result.status !== 'success') {
      return result;
    }

    refreshTiles();
    void startPolling();

    return result;
  };

  const internalAccounts = options?.options?.find(
    (option) =>
      option.displayGroup === LendingPaymentSourceDisplayGroup.Internal,
  );
  const checkingAccount = internalAccounts?.details?.find(
    (account) => account.source === LendingPaymentSource.InternalDda,
  );
  const savingsAccount = internalAccounts?.details?.find(
    (account) => account.source === LendingPaymentSource.InternalSavings,
  );
  const autoSelectedAccount = getDefaultSelectedAccount(
    checkingAccount,
    savingsAccount,
  );
  const selectedAccount = selectedAccountId
    ? options?.options
        .flatMap((option) => option.details)
        .find((account) => account.accountId === selectedAccountId)
    : undefined;

  const setSelectedAccount = (account: LendingPaymentOptionDetails) => {
    setSelectedAccountId(account.accountId);
  };

  return {
    paymentOptions: options,
    openPlaid: startLinkAccount,
    ready,
    autoSelectedAccount,
    selectedAccount,
    setSelectedAccount,
    refetch,
    loading: isPolling || loading || fetching || fetchingHomeTiles,
    error:
      paymentOptionsError ||
      plaidError ||
      homeTilesError ||
      pollingError ||
      null,
  };
};

export default useAdvancePaymentAccountController;
